import * as React from "react";
import { InputTextarea } from "@bizform/primereact/components/inputtextarea/InputTextarea";
import { InputTextareaProps } from "@bizform/primereact/inputtextarea";
import TooltipOptions from "@bizform/primereact/components/tooltip/TooltipOptions";
import { Static } from "@bizform/type/decorator/Static.decorator";
import {
    BaseStateModel,
    ComponentElement,
    ComponentElementDisplayMode,
    ComponentElementStatic,
    OnChangeFnType
} from "@bizform/type/model/component_element/ComponentElement.model";
import {
    ComponentCategory,
    ComponentElementManifest,
    ComponentElementProperty
} from "@bizform/type/model/component_element/ComponentElementManifest.model";
import { DataType } from "@bizform/type/model/model/DataType.model";
import { ComponentHelper, Debug } from "@bizform/helper/dist/js/bizflow/helper.min";
import { ComponentElementClass, ReactComponentElementInit } from "@bizform/core/dist/js/bizflow/core.min";
import { AbstractComponentElement } from "../AbstractComponentElement";
import { I18n } from "../../misc/model/I18n.model";


type Props = InputTextareaProps & {
};

type WrappedCompState = {
    // attribute
    autoResize?: boolean;
    tabindex?: number;
    value?: string;
    readOnly?: boolean;
    disabled?: boolean;
    rows?: number;
    maxLength?: number;
    placeholder?: string;
    printStyle?: object;
    printClassName?: string;
    tooltipAttribute?: TooltipOptions & {text?: string};
    // event
    onChange?: OnChangeFnType<React.FormEvent<HTMLTextAreaElement>>;
};

type WrappedFormState = {
    onChange?: string;
};

type State = BaseStateModel & {
    wrappedCompState: WrappedCompState;
    wrappedFormState: WrappedFormState;
};

/**
 * @since 0.0.1
 * @author Jongkwang Park
 * @class BfInputTextarea
 * @description BfInputTextarea component element
 */
@ComponentElementClass()
@Static<ComponentElementStatic<string>>()
export class BfInputTextarea extends AbstractComponentElement<Props, State> {
    /* manifest */
    static readonly componentElementManifest: ComponentElementManifest = {
        uniqueId: "_fODk7_1NS77CO0G9eMRLVH2OqV6zP3L",
        category: ComponentCategory.INPUT,
        label: I18n.componentElement.inputtextarea.label,
        description: I18n.componentElement.inputtextarea.description,
        icon: "",
        dataType: [DataType.STRING, DataType.NUMBER, DataType.INTEGER],
        property: null
    };

    focus(): void {
        if (this._inputTextarea) {
            this._inputTextarea.focus();
        }
    }

    setProperty(property: ComponentElementProperty): void {
        try {
            const newState = ComponentHelper.createStateWithProperties<State, React.FormEvent<HTMLTextAreaElement>>(property, this);
            this.setState(newState);
        } catch (e) {
            this.emitDidCatch(e);
        }
    }

    emit(value: string): void {
    }

    emitFormState(value: string): void {
    }

    subscription(value: string): void {
        if (value !== this.state.wrappedCompState.value) {
            const newState = {
                wrappedCompState: {
                    ...this.state.wrappedCompState,
                    value: ""
                }
            };
            newState.wrappedCompState.value = (value === null || value === undefined) ? "" : value;
            this.setState(newState);
        }
    }

    @ReactComponentElementInit(BfInputTextarea)
    static init(): ComponentElement<string> {
        return;
    }

    initDone(): void {
    }

    destroy(): void {
        this.destroyDone();
    }

    destroyDone(): void {
    }

    /* for React */
    props: Props;
    state: State;

    private _inputTextarea: HTMLTextAreaElement;

    constructor(props?: Props) {
        super(props);

        this.state = {
            show: false,
            displayMode: ComponentElementDisplayMode.INIT,
            wrappedCompState: {},
            wrappedFormState: {}
        };

        this._onChangeEvent = this._onChangeEvent.bind(this);
        this._setRef = this._setRef.bind(this);
    }

    componentWillUnmount(): void {
        this._inputTextarea = null;
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
        this.emitDidCatch(error);
    }

    emitDidCatch(error: Error | any): void {
        Debug.console.error(error);
    }

    emitSubscriptionReject(): void {
    }

    private _onChangeEvent(event): void {
        const newState = {
            wrappedCompState: {
                ...this.state.wrappedCompState,
                value: event.target.value
            }
        };

        this.setState(newState, this._callbackSetState);
    }

    private _callbackSetState() {
        this.emit(this.state.wrappedCompState.value);
    }

    render(): React.ReactNode {
        const {
            show,
            displayMode
        } = this.state;

        if (!show) {
            return null;
        } else {
            switch (displayMode) {
                case ComponentElementDisplayMode.EDIT:
                    return this._render4Edit();
                case ComponentElementDisplayMode.READONLY:
                    return this._render4ReadOnly();
                case ComponentElementDisplayMode.DISABLED:
                    return this._render4Disabled();
                case ComponentElementDisplayMode.PRINT:
                    return this._render4Print();
                default:
                    return null;
            }
        }
    }

    private _render(newProps: Props): React.ReactNode {
        const key = ComponentHelper.tooltipProps(newProps, this.elementId, this.accessibility);
        delete(newProps["printClassName"]);
        delete(newProps["printStyle"]);
        if (newProps.value === null) newProps.value = "";
        return (
            <InputTextarea
                {...newProps}
                id={this.elementId}
                key={key}
                ref={this._setRef}
            />
        );
    }

    private _render4Edit(): React.ReactNode {
        const newProps = {
            ...this.state.wrappedCompState
        };

        return this._render(newProps);
    }

    private _render4ReadOnly(): React.ReactNode {
        const newProps = {
            ...this.state.wrappedCompState,
            readOnly: true
        };

        return this._render(newProps);
    }

    private _render4Disabled(): React.ReactNode {
        const newProps = {
            ...this.state.wrappedCompState,
            disabled: true
        };

        return this._render(newProps);
    }

    private _render4Print(): React.ReactNode {
        return (
            <div ref={this._setRef} style={this.state.wrappedCompState ? {...this.state.wrappedCompState.printStyle} : null} className={`bf-print-mode ${this.state.wrappedCompState ? this.state.wrappedCompState.printClassName : null}`}>{this.state.wrappedCompState.value}</div>
        );
    }

    private _setRef(ref) {
        if (ref) {
            this._inputTextarea = ref.element;
            this.readyComponent();
        }
    }
}
